import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Menu, User, LogOut, LayoutDashboard, ShieldCheck, FileSearch } from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/news", label: "News" },
  { to: "/track-order", label: "Track Order" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { settings } = useSiteSettings();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuPages, setMenuPages] = useState([]);

  useEffect(() => {
    api.get("/pages").then((r) => {
      setMenuPages((r.data.pages || []).filter((p) => p.show_in_menu));
    }).catch(() => setMenuPages([]));
  }, []);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
          {settings?.logo_url ? (
            <img src={settings.logo_url} alt="Way2Efiling" className="h-9 object-contain" />
          ) : (
            <>
              <span className="inline-flex w-9 h-9 rounded-md bg-[#1E3A8A] items-center justify-center text-white font-bold font-display">W2</span>
              <div className="leading-tight">
                <div className="font-display font-bold text-[#0A192F] text-lg">Way2Efiling</div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 -mt-0.5">Tax • License • Compliance</div>
              </div>
            </>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-[#1E3A8A] bg-blue-50" : "text-slate-600 hover:text-[#1E3A8A] hover:bg-slate-50"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {menuPages.map((p) => (
            <NavLink
              key={p.slug}
              to={`/pages/${p.slug}`}
              data-testid={`nav-cms-${p.slug}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-[#1E3A8A] bg-blue-50" : "text-slate-600 hover:text-[#1E3A8A] hover:bg-slate-50"
                }`
              }
            >
              {p.title}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {!user ? (
            <>
              <Button variant="ghost" className="text-slate-700" onClick={() => nav("/login")} data-testid="nav-login-btn">
                Sign In
              </Button>
              <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={() => nav("/register")} data-testid="nav-register-btn">
                Get Started
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="user-menu-trigger">
                  <User className="w-4 h-4" /> {user.full_name?.split(" ")[0] || "Account"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs text-slate-500">Signed in as</div>
                  <div className="text-sm font-medium truncate">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "admin" ? (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Admin Console
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> My Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => nav("/track-order")}>
                  <FileSearch className="w-4 h-4 mr-2" /> Track an Order
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-md hover:bg-slate-100"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md text-sm ${isActive ? "text-[#1E3A8A] bg-blue-50" : "text-slate-700"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-2 flex gap-2">
              {!user ? (
                <>
                  <Button variant="outline" className="flex-1" onClick={() => { setOpen(false); nav("/login"); }}>Sign In</Button>
                  <Button className="flex-1 bg-[#1E3A8A]" onClick={() => { setOpen(false); nav("/register"); }}>Get Started</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="flex-1" onClick={() => { setOpen(false); nav(user.role === "admin" ? "/admin" : "/dashboard"); }}>Dashboard</Button>
                  <Button className="flex-1 bg-[#1E3A8A]" onClick={() => { setOpen(false); logout(); nav("/"); }}>Logout</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
